import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  Checkbox,
  TextField,
  InputAdornment,
  Divider,
  styled,
  Tabs,
  Tab,
  Tooltip,
  IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Healing as DiagnosticIcon,
  AccountTree as DecisionIcon,
  Insights as PredictiveIcon,
  Create as CreativeIcon,
  Info as InfoIcon
} from '@mui/icons-material';

interface ToolItem {
  id: string;
  name: string;
  description: string;
  checked: boolean;
  cost: number;
  tooltip?: string;
}

interface ToolCategory {
  id: string;
  title: string;
  icon: React.ReactElement;
  tools: ToolItem[];
}

interface ProblemSolvingToolboxProps {
  problemStatement?: string;
  onCostChange?: (cost: number) => void;
  currentCost?: number;
}

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(138, 43, 226, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.2)',
    '&::before': {
      opacity: 0.1,
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    zIndex: -1,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -5,
    left: 0,
    width: '100%',
    height: 3,
    background: 'linear-gradient(90deg, #00CC99, #8A2BE2)',
    borderRadius: 2,
  }
}));

const ToolCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  }
}));

const TotalContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  background: 'rgba(0, 204, 153, 0.1)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#00CC99',
  },
  minHeight: '72px',
  minWidth: '120px',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: '#00CC99',
  },
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
}));

const ProblemSolvingToolbox: React.FC<ProblemSolvingToolboxProps> = ({
  problemStatement = "Define your problem here...",
  onCostChange,
  currentCost = 0
}) => {
  // Initial tool categories with their tools, categorized by best-use scenarios
  const initialToolCategories: ToolCategory[] = [
    {
      id: 'diagnostic',
      title: 'Diagnostic Methods',
      icon: <DiagnosticIcon />,
      tools: [
        { 
          id: 'diagnostic1', 
          name: 'SWOT Analysis', 
          description: 'Analyze Strengths, Weaknesses, Opportunities, and Threats', 
          checked: false, 
          cost: 1500,
          tooltip: 'Best used for strategic planning, product development, or risk assessment. Provides a comprehensive overview of internal and external factors.'
        },
        { 
          id: 'diagnostic2', 
          name: 'Five Whys', 
          description: 'Iterative interrogative technique to determine root causes', 
          checked: false, 
          cost: 800,
          tooltip: 'Works well for operational issues but may need deeper validation in complex systems. Best for day-to-day problems with relatively straightforward causes.'
        },
        { 
          id: 'diagnostic3', 
          name: 'Root Cause Analysis', 
          description: 'Identify the origin of a problem', 
          checked: false, 
          cost: 1900,
          tooltip: 'Ideal for technical problems, quality issues, and safety incidents. Consider using the Fishbone Diagram (Ishikawa) as a complementary tool.'
        }
      ]
    },
    {
      id: 'decision',
      title: 'Decision-Making Methods',
      icon: <DecisionIcon />,
      tools: [
        { 
          id: 'decision1', 
          name: 'Decision Matrix', 
          description: 'Evaluate and prioritize options based on weighted criteria', 
          checked: false, 
          cost: 1000,
          tooltip: 'Best used when multiple criteria need to be weighed objectively. Excellent for investments, vendor selection, hiring decisions, and product feature prioritization.'
        },
        { 
          id: 'decision2', 
          name: 'PDCA Cycle', 
          description: 'Plan-Do-Check-Act methodology for continuous improvement', 
          checked: false, 
          cost: 1200,
          tooltip: 'Ideal for iterative improvements, often used in Lean/Agile environments. Perfect for process optimization, quality management, and operational excellence initiatives.'
        },
        { 
          id: 'decision3', 
          name: 'Scenario Planning', 
          description: 'Develop alternative future scenarios', 
          checked: false, 
          cost: 2800,
          tooltip: 'Used for crisis management, strategic planning, and adapting to future trends. Industries like energy, finance, and healthcare rely on this for long-term planning.'
        }
      ]
    },
    {
      id: 'predictive',
      title: 'Predictive & Analytical',
      icon: <PredictiveIcon />,
      tools: [
        { 
          id: 'predictive1', 
          name: 'Data Visualization', 
          description: 'Represent data graphically to identify patterns', 
          checked: false, 
          cost: 3000,
          tooltip: 'Best when working with large datasets. Essential for communicating insights to stakeholders and discovering hidden patterns in complex data.'
        },
        { 
          id: 'predictive2', 
          name: 'Predictive Modeling', 
          description: 'Use statistical techniques to forecast outcomes', 
          checked: false, 
          cost: 4500,
          tooltip: 'Requires quality historical data to be effective. Powerful for sales forecasting, risk management, resource planning, and customer behavior analysis.'
        },
        { 
          id: 'predictive3', 
          name: 'A/B Testing', 
          description: 'Compare two versions to determine which performs better', 
          checked: false, 
          cost: 2200,
          tooltip: 'Best suited for marketing, UX design, and product decisions. Essential for data-driven organizations focused on continuous improvement and conversion optimization.'
        }
      ]
    },
    {
      id: 'creative',
      title: 'Creative Problem-Solving',
      icon: <CreativeIcon />,
      tools: [
        { 
          id: 'creative1', 
          name: 'Design Thinking', 
          description: 'Human-centered approach to innovation', 
          checked: false, 
          cost: 2500,
          tooltip: 'A five-phase process: Empathize, Define, Ideate, Prototype, Test. Particularly effective for consumer products, services, and user experience challenges.'
        },
        { 
          id: 'creative2', 
          name: 'Lateral Thinking', 
          description: 'Solving problems through indirect and creative approaches', 
          checked: false, 
          cost: 1700,
          tooltip: 'Edward de Bono\'s "Six Thinking Hats" approach provides a structured way to implement lateral thinking in group settings. Great for breaking out of conventional thought patterns.'
        },
        { 
          id: 'creative3', 
          name: 'Brainstorming (SCAMPER)', 
          description: 'Generate creative ideas using structured techniques', 
          checked: false, 
          cost: 1500,
          tooltip: 'Use structured brainstorming techniques like "SCAMPER" (Substitute, Combine, Adapt, Modify, Put to other uses, Eliminate, Reverse) or "Crazy 8s" for more effective ideation sessions.'
        },
        { 
          id: 'creative4', 
          name: 'First Principles Thinking', 
          description: 'Break down complex problems into basic elements', 
          checked: false, 
          cost: 2000,
          tooltip: 'Often used by entrepreneurs like Elon Musk to rethink industry norms. Perfect for disruptive innovation and challenging established assumptions.'
        }
      ]
    },
    {
      id: 'strategic',
      title: 'Strategic Innovation',
      icon: <SearchIcon />,
      tools: [
        { 
          id: 'strategic1', 
          name: 'Systems Thinking', 
          description: 'Consider how components interact within the larger system', 
          checked: false, 
          cost: 1800,
          tooltip: 'Applies to interconnected business problems, supply chains, organizational change, and global challenges. Helps identify leverage points for maximum impact.'
        },
        { 
          id: 'strategic2', 
          name: 'Blue Ocean Strategy', 
          description: 'Create uncontested market space', 
          checked: false, 
          cost: 3500,
          tooltip: 'Focuses on differentiation and cost leadership simultaneously. Ideal for businesses looking to create new market spaces rather than competing in existing ones.'
        },
        { 
          id: 'strategic3', 
          name: 'Disruptive Innovation', 
          description: 'Transform existing markets or create new ones', 
          checked: false, 
          cost: 4000,
          tooltip: 'Based on Clayton Christensen\'s work on how industries are reshaped. Particularly relevant for technology companies and startups looking to challenge incumbents.'
        }
      ]
    }
  ];

  const [toolCategories, setToolCategories] = useState<ToolCategory[]>(initialToolCategories);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  // Calculate total cost whenever tool selections or costs change
  useEffect(() => {
    const newTotal = toolCategories.reduce((categoryTotal, category) => {
      return categoryTotal + category.tools.reduce((toolTotal, tool) => {
        return toolTotal + (tool.checked ? tool.cost : 0);
      }, 0);
    }, 0);
    
    setTotalCost(newTotal);
    
    // Call the onCostChange prop when the total changes
    if (onCostChange) {
      onCostChange(newTotal);
    }
  }, [toolCategories, onCostChange]);

  // Handle checkbox change
  const handleCheckboxChange = (categoryId: string, toolId: string, checked: boolean) => {
    setToolCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              tools: category.tools.map(tool => 
                tool.id === toolId 
                  ? { ...tool, checked }
                  : tool
              )
            }
          : category
      )
    );
  };

  // Handle tool name change
  const handleToolNameChange = (categoryId: string, toolId: string, name: string) => {
    setToolCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              tools: category.tools.map(tool => 
                tool.id === toolId 
                  ? { ...tool, name }
                  : tool
              )
            }
          : category
      )
    );
  };

  // Handle tool description change
  const handleToolDescriptionChange = (categoryId: string, toolId: string, description: string) => {
    setToolCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              tools: category.tools.map(tool => 
                tool.id === toolId 
                  ? { ...tool, description }
                  : tool
              )
            }
          : category
      )
    );
  };

  // Handle tool cost change
  const handleToolCostChange = (categoryId: string, toolId: string, costStr: string) => {
    const cost = parseFloat(costStr) || 0;
    setToolCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              tools: category.tools.map(tool => 
                tool.id === toolId 
                  ? { ...tool, cost }
                  : tool
              )
            }
          : category
      )
    );
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <SectionContainer>
      <SectionTitle variant="h5">
        Problem Solving Toolbox
      </SectionTitle>
      
      <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
        Select the tools and methodologies you&apos;d like to apply to your problem. Choose categories below to view different tool types.
      </Typography>
      
      {/* Category Tabs */}
      <StyledTabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="problem-solving method categories"
      >
        {toolCategories.map((category, index) => (
          <StyledTab 
            key={category.id} 
            icon={category.icon} 
            label={category.title} 
            id={`tab-${category.id}`}
            aria-controls={`tabpanel-${category.id}`}
          />
        ))}
      </StyledTabs>
      
      {/* Display only the selected category */}
      {toolCategories.map((category, categoryIndex) => (
        <div
          key={category.id}
          role="tabpanel"
          hidden={selectedTab !== categoryIndex}
          id={`tabpanel-${category.id}`}
          aria-labelledby={`tab-${category.id}`}
        >
          {selectedTab === categoryIndex && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ToolCard sx={{ mb: 3 }}>
                <List>
                  {category.tools.map((tool) => (
                    <ListItem key={tool.id} alignItems="flex-start" sx={{ flexDirection: 'column', py: 2 }}>
                      <Box sx={{ display: 'flex', width: '100%', mb: 1, alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 42 }}>
                          <Checkbox
                            checked={tool.checked}
                            onChange={(e) => handleCheckboxChange(category.id, tool.id, e.target.checked)}
                            icon={<CheckIcon sx={{ color: 'rgba(255, 255, 255, 0.3)' }} />}
                            checkedIcon={<CheckIcon sx={{ color: '#00CC99' }} />}
                          />
                        </ListItemIcon>
                        <TextField
                          fullWidth
                          variant="standard"
                          value={tool.name}
                          onChange={(e) => handleToolNameChange(category.id, tool.id, e.target.value)}
                          sx={{ 
                            input: { 
                              color: tool.checked ? '#00CC99' : '#fff',
                              fontWeight: tool.checked ? 600 : 400,
                            }
                          }}
                        />
                        {tool.tooltip && (
                          <Tooltip title={tool.tooltip} placement="top">
                            <IconButton size="small" sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <TextField
                          variant="outlined"
                          size="small"
                          value={tool.cost}
                          onChange={(e) => handleToolCostChange(category.id, tool.id, e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          sx={{ 
                            width: '120px', 
                            ml: 2,
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: 'rgba(138, 43, 226, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(138, 43, 226, 0.5)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#8A2BE2',
                              },
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ pl: 5, width: '100%' }}>
                        <TextField
                          fullWidth
                          variant="standard"
                          value={tool.description}
                          onChange={(e) => handleToolDescriptionChange(category.id, tool.id, e.target.value)}
                          sx={{ 
                            input: { 
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </ToolCard>
            </motion.div>
          )}
        </div>
      ))}
      
      <Divider sx={{ my: 3, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
      
      <TotalContainer>
        <Typography variant="h6" sx={{ color: '#00CC99' }}>
          Total Cost:
        </Typography>
        <Typography variant="h6" sx={{ color: '#00CC99', fontWeight: 600 }}>
          ${totalCost.toLocaleString()}
        </Typography>
      </TotalContainer>
    </SectionContainer>
  );
};

export default ProblemSolvingToolbox; 